import { useEffect, useMemo, useState } from "react"

import type { PresentationDeckPreview } from "./presentation-deck-utils"

import { ChevronDown, ChevronLeft, ChevronRight, FileText, Presentation } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { sessionFileContentUrl } from "@/features/chat/utils/image-activities"
import { imageMimeTypeForFilename } from "@/features/chat/utils/image-file-mime"
import { DownloadButton } from "@/features/file-viewer/components/download-button"
import { useFileContent } from "@/hooks/use-file-content"
import { cn } from "@/lib/utils"

type PresentationDeckCardProps = {
  deck: PresentationDeckPreview
  sessionId: string
}

type SessionImageProps = {
  sessionId: string
  filename: string
  alt: string
  className?: string
}

function SessionImage({ sessionId, filename, alt, className }: SessionImageProps) {
  const url = sessionFileContentUrl(sessionId, filename)
  const { data: buffer } = useFileContent(url)
  const [objectUrl, setObjectUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!buffer) {
      setObjectUrl(null)
      return
    }
    const blob = new Blob([buffer], { type: imageMimeTypeForFilename(filename) })
    const next = URL.createObjectURL(blob)
    setObjectUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [buffer, filename])

  if (!objectUrl) {
    return <span aria-hidden className={cn("block h-full w-full", className)} />
  }
  return <img alt={alt} className={className} loading="lazy" src={objectUrl} />
}

export function PresentationDeckCard({ deck, sessionId }: PresentationDeckCardProps) {
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const images = deck.previewImages
  const activeImage = images[Math.min(activeIndex, Math.max(0, images.length - 1))]

  const slideLabel = useMemo(() => {
    const count = deck.slideCount || images.length
    return count === 1 ? "1 slide" : `${count} slides`
  }, [deck.slideCount, images.length])

  const showPrevious = () => {
    setActiveIndex((current) => (current === 0 ? images.length - 1 : current - 1))
  }

  const showNext = () => {
    setActiveIndex((current) => (current + 1) % images.length)
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="mt-2 overflow-hidden">
        <CardContent className="p-0">
          <section className="flex items-center gap-3 p-3">
            <button
              aria-label={`Preview ${deck.filename}`}
              className="bg-muted h-16 w-28 shrink-0 overflow-hidden rounded-md border"
              type="button"
              onClick={() => setOpen((value) => !value)}
            >
              {images[0] ? (
                <SessionImage
                  alt=""
                  className="h-full w-full object-contain"
                  filename={images[0]}
                  sessionId={sessionId}
                />
              ) : (
                <span className="flex h-full w-full items-center justify-center">
                  <Presentation className="text-muted-foreground size-7" />
                </span>
              )}
            </button>

            <section className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Presentation className="text-muted-foreground size-4 shrink-0" />
                <p className="truncate text-sm font-medium" title={deck.filename}>
                  {deck.filename}
                </p>
              </div>
              <p className="text-muted-foreground text-xs">{slideLabel}</p>
            </section>

            <section className="flex shrink-0 items-center gap-1">
              <DownloadButton
                fileUrl={sessionFileContentUrl(sessionId, deck.filename)}
                title={deck.filename}
              />
              <CollapsibleTrigger asChild>
                <Button
                  aria-label={open ? "Hide slide previews" : "Preview slides"}
                  className="gap-1"
                  size="sm"
                  variant="ghost"
                >
                  <FileText className="size-4" />
                  <span className="hidden sm:inline">Preview</span>
                  <ChevronDown
                    className={cn("size-4 transition-transform", open && "rotate-180")}
                  />
                </Button>
              </CollapsibleTrigger>
            </section>
          </section>

          <CollapsibleContent>
            <section className="border-t p-3">
              {activeImage && (
                <section className="relative overflow-hidden rounded-md border bg-black/5">
                  <SessionImage
                    alt={`Slide ${activeIndex + 1} of ${images.length}`}
                    className="aspect-video w-full object-contain"
                    filename={activeImage}
                    sessionId={sessionId}
                  />
                  {images.length > 1 && (
                    <>
                      <Button
                        aria-label="Previous slide"
                        className="bg-background/85 absolute top-1/2 left-2 -translate-y-1/2"
                        size="icon"
                        type="button"
                        variant="icon"
                        onClick={showPrevious}
                      >
                        <ChevronLeft className="size-5" />
                      </Button>
                      <Button
                        aria-label="Next slide"
                        className="bg-background/85 absolute top-1/2 right-2 -translate-y-1/2"
                        size="icon"
                        type="button"
                        variant="icon"
                        onClick={showNext}
                      >
                        <ChevronRight className="size-5" />
                      </Button>
                    </>
                  )}
                </section>
              )}

              {images.length > 1 && (
                <section className="mt-3 flex gap-2 overflow-x-auto pb-1">
                  {images.map((image, index) => (
                    <button
                      key={image}
                      aria-label={`Show slide ${index + 1}`}
                      type="button"
                      className={cn(
                        "h-14 w-24 shrink-0 overflow-hidden rounded border bg-black/5",
                        index === activeIndex && "border-primary ring-primary/30 ring-2"
                      )}
                      onClick={() => setActiveIndex(index)}
                    >
                      <SessionImage
                        alt=""
                        className="h-full w-full object-contain"
                        filename={image}
                        sessionId={sessionId}
                      />
                    </button>
                  ))}
                </section>
              )}
            </section>
          </CollapsibleContent>
        </CardContent>
      </Card>
    </Collapsible>
  )
}
